import { useState, useEffect, useCallback } from "react"
import { Link } from "react-router-dom"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Search, Eye, RefreshCw, ArrowLeftRight } from "lucide-react"
import { useAdminAuth } from "@/context/Admin"
import Loader from "@/components/loader/loader"
import { formatCurrency } from "@/utils/format"
import { format } from "date-fns"
import { toast } from "sonner"

export default function Returns() {
  const { getAllReturnRequests } = useAdminAuth()
  
  const [returns, setReturns] = useState([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState("all")
  const [orderIdFilter, setOrderIdFilter] = useState("")
  const [searchTerm, setSearchTerm] = useState("")

  const fetchReturns = useCallback(async () => {
    setLoading(true)
    try {
      const filters = {}
      if (statusFilter && statusFilter !== "all") filters.status = statusFilter
      if (orderIdFilter) filters.orderId = orderIdFilter
      
      const response = await getAllReturnRequests(filters)
      let returnRequests = response.returnRequests || []
      
      // Apply search filter on client side
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase()
        returnRequests = returnRequests.filter((ret) => {
          return (
            ret.returnId?.toString().toLowerCase().includes(searchLower) ||
            ret.orderId?.toString().toLowerCase().includes(searchLower) ||
            ret.user?.name?.toLowerCase().includes(searchLower) ||
            ret.user?.email?.toLowerCase().includes(searchLower) ||
            ret.reason?.toLowerCase().includes(searchLower)
          )
        })
      }
      
      setReturns(returnRequests)
    } catch (error) {
      console.error("Error fetching return requests:", error)
      setReturns([])
    } finally {
      setLoading(false)
    }
  }, [getAllReturnRequests, statusFilter, orderIdFilter, searchTerm])

  useEffect(() => {
    fetchReturns()
  }, [fetchReturns])

  const getStatusBadge = (status) => {
    const variants = {
      Pending: "bg-yellow-100 text-yellow-700 border-yellow-300",
      Approved: "bg-blue-100 text-blue-700 border-blue-300",
      Rejected: "bg-red-100 text-red-700 border-red-300",
      Processing: "bg-purple-100 text-purple-700 border-purple-300",
      Completed: "bg-green-100 text-green-700 border-green-300",
    }
    return variants[status] || "bg-gray-100 text-gray-700 border-gray-300"
  }

  if (loading) {
    return <Loader />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Return Requests</h1>
          <p className="text-muted-foreground">Manage customer return requests and process refunds</p>
        </div>
        <Button variant="outline" onClick={fetchReturns} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search by return ID, order ID, customer name, or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter || "all"} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Pending">Pending</SelectItem>
                <SelectItem value="Approved">Approved</SelectItem>
                <SelectItem value="Rejected">Rejected</SelectItem>
                <SelectItem value="Processing">Processing</SelectItem>
                <SelectItem value="Completed">Completed</SelectItem>
              </SelectContent>
            </Select>
            <Input
              placeholder="Filter by Order ID..."
              value={orderIdFilter}
              onChange={(e) => setOrderIdFilter(e.target.value)}
              className="w-full sm:w-[180px]"
            />
          </div>
        </CardContent>
      </Card>

      {/* Returns Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Return Requests</CardTitle>
          <CardDescription>
            {returns.length} return request{returns.length !== 1 ? "s" : ""} found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {returns.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <ArrowLeftRight className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg">No return requests found</p>
              <p className="text-sm mt-2">Return requests will appear here once customers submit them</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Return ID</TableHead>
                    <TableHead>Order ID</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Order Total</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Requested</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {returns.map((returnReq) => (
                    <TableRow key={returnReq.returnId}>
                      <TableCell className="font-mono text-sm">
                        #{returnReq.returnId}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        <Link 
                          to={`/admin/orders/${returnReq.orderId}`}
                          className="text-primary hover:underline"
                        >
                          #{returnReq.orderId}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{returnReq.user?.name || "N/A"}</div>
                          <div className="text-sm text-muted-foreground">{returnReq.user?.email || ""}</div>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">
                        {returnReq.reason || "—"}
                      </TableCell>
                      <TableCell>
                        {formatCurrency(returnReq.orderTotal || 0)}
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusBadge(returnReq.status)}>
                          {returnReq.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {returnReq.createdAt 
                          ? format(new Date(returnReq.createdAt), "MMM dd, yyyy")
                          : "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        <Link to={`/admin/returns/${returnReq.returnId}`}>
                          <Button variant="outline" size="sm">
                            <Eye className="h-4 w-4 mr-2" />
                            View
                          </Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

